import {call, put, race, take, delay} from 'redux-saga/effects';

import {
    cancelPasswordChange,
    cancelPasswordReset,
    changePasswordForUser,
    completePasswordReset,
    exitPasswordReset,
    passwordResetCancelled,
    passwordResetCodeSuccess,
    passwordResetError,
    passwordResetSuccess,
    requestPasswordResetCode,
    setInvalidResetCode,
    setPasswordResetState,
    submitForgottenPasswordCode,
    submitForgottenPasswordEmail,
    submitNewPassword,
    validatePasswordResetCode,
    validateResetCodeSuccess
} from 'modules/authentication/actions';
import {passwordResetStates} from 'modules/authentication/constants';
import {doApiCall} from 'modules/sagaUtils';
import {clearPasswordResetData, getPasswordResetId, setPasswordResetId} from 'modules/authentication/utils';

export function* removeLocalResetId(state) {
    if (state.payload != passwordResetStates.ENTER_CODE) {
        clearPasswordResetData();
    }
}

export function* passwordResetFlow() {

    while (true) {

        let resetId = getPasswordResetId();
        if (!resetId) {
            const submittedEmail = yield race({
                confirm: take(submitForgottenPasswordEmail),
                cancel: take(cancelPasswordReset),
            })

            if (submittedEmail.cancel) {
                yield endPasswordResetFlowWithStatus();
                continue;
            }

            const email = submittedEmail.confirm.payload;

            resetId = yield* getNewResetId(email);
            if (!resetId) {
                yield endPasswordResetFlowWithStatus(passwordResetStates.RESET_FAILED);
                continue;
            }
        }

        yield put(setPasswordResetState(passwordResetStates.ENTER_CODE))

        yield delay(10); // to allow the prior action to have fully processed in state

        let validCode = yield validResetCodeSupplied(resetId);

        if (!validCode) {
            yield cancelPasswordChangeCall(resetId);
            yield endPasswordResetFlowWithStatus(passwordResetStates.RESET_CANCELLED);
            continue;
        }

        yield put(setPasswordResetState(passwordResetStates.ENTER_PASSWORDS));

        const submitPasswordAction = yield race({
            confirm: take(submitNewPassword),
            cancel: take(cancelPasswordReset),
        });

        if (submitPasswordAction.cancel) {
            yield cancelPasswordChangeCall(resetId);
            yield endPasswordResetFlowWithStatus(passwordResetStates.RESET_CANCELLED);
            continue;
        }

        const newPassword = submitPasswordAction.confirm.payload.password;

        try {
            yield call(
                doApiCall,
                changePasswordForUser,
                {resetId, newPassword},
                null,
                passwordResetSuccess,
                passwordResetError,
            );
        } catch (e) {
            yield endPasswordResetFlowWithStatus(passwordResetStates.RESET_FAILED);
            continue;
        }

        yield endPasswordResetFlowWithStatus(passwordResetStates.RESET_SUCCESS);
    }
}

function* endPasswordResetFlowWithStatus(passwordResetState) {
    if (passwordResetState) {
        yield put(setPasswordResetState(passwordResetState));
        yield take(completePasswordReset);
    }
    yield put(exitPasswordReset());
    yield put(setPasswordResetState(passwordResetStates.ENTER_EMAIL));
}

function* getNewResetId(email) {

    let resetIdCall;
    try {
        resetIdCall = yield call(
            doApiCall,
            requestPasswordResetCode,
            {email},
            null,
            passwordResetCodeSuccess,
            passwordResetError,
        );
    } catch (e) {
        return null;
    }

    const resetId = resetIdCall?.payload?.data?.resetId;
    if (resetId){
        setPasswordResetId(resetId);
    }
    return resetId;
}

function* validResetCodeSupplied(resetId) {

    while (true) {
        const submittedCode = yield race({
            confirm: take(submitForgottenPasswordCode),
            cancel: take(cancelPasswordReset),
            stateChange: take(setPasswordResetState)
        });

        yield put(setInvalidResetCode(false));

        if (!submittedCode.confirm) {
            return false;
        }

        const resetCode = submittedCode.confirm.payload.toUpperCase();

        try {
            yield call(
                doApiCall,
                validatePasswordResetCode,
                {resetId, resetCode},
                null,
                validateResetCodeSuccess,
                passwordResetError,
            );
        } catch (e) {
            yield put(setInvalidResetCode(true));
            continue;
        }
        return true;
    }
}

function* cancelPasswordChangeCall(resetId) {
    try {
        yield call(
            doApiCall,
            cancelPasswordChange,
            {resetId},
            null,
            passwordResetCancelled,
            passwordResetError,
        );
    } catch (e) {
        console.error('There was an error cancelling the password reset request', e);
    }
}
